import React, { useEffect, useRef, useState } from 'react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { EMBEDDED_CONFLUENCE_SIDEPANEL_CONTAINER_ID } from '@jira/confluence__confluence-integration-controls/src/constants.tsx';
import {
	EmbeddedConfluenceSidepanelContainer,
	EmbeddedConfluenceSidepanelContainerWithIssueKey,
	useEmbeddedConfluenceSidePanel,
} from '@jira/confluence__confluence-integration-controls/src/controllers/use-embedded-confluence-side-panel/index.tsx';
import { useActiveRightSidebarState } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component/src/index.tsx';
import { xcss } from '@atlaskit/primitives/xcss';
import Box from '@atlaskit/primitives/box';
import { useEmbeddedPanelEntryPoint } from './useEmbeddedPanelEntryPoint.tsx';

const DELAY = 310;

const PanelInLayout = (): React.JSX.Element | null => {
	const rightSidebarCurrentState = useActiveRightSidebarState();
	const [{ panel: isOpen }] = useEmbeddedConfluenceSidePanel();

	const [localPanelId, setLocalPanelId] = useState<unknown>();
	const prevPanelIdRef = useRef<unknown>();
	const delayRef = useRef<unknown>(null);

	const panelId = rightSidebarCurrentState?.panelId;
	const { entryPointActions, entryPoint } = useEmbeddedPanelEntryPoint();

	useEffect(() => {
		if (isOpen) {
			entryPointActions?.load();
		}
	}, [isOpen, entryPointActions, localPanelId]);
	/**
	 * This use effect is used to add (when is necessary) a delay
	 * in the panelId => localPanelId syncing
	 */
	useEffect(() => {
		// @ts-expect-error - TS2769 - No overload matches this call.
		clearTimeout(delayRef.current);

		if (prevPanelIdRef.current === EMBEDDED_CONFLUENCE_SIDEPANEL_CONTAINER_ID) {
			/**
			 * The right-sidebar is showing the embedded confluence content...
			 */
			if (
				rightSidebarCurrentState &&
				panelId !== undefined &&
				!rightSidebarCurrentState.isCollapsed &&
				!rightSidebarCurrentState.isMinimised
			) {
				/**
				 * and is open, not collapsed and we want to show a panel
				 * with an ID !== EMBEDDED_CONFLUENCE_SIDEPANEL_CONTAINER_ID
				 */
				setLocalPanelId(panelId);
			} else {
				/**
				 * and we want to close it
				 */
				delayRef.current = setTimeout(() => {
					setLocalPanelId(panelId);

					// TODO: trigger close analytics event
				}, DELAY);
			}
		} else {
			/**
			 * If the right sidebar is not showing the embedded confluence content,
			 * we just keep localPanelId in sync with panelId
			 */
			setLocalPanelId(panelId);
		}

		prevPanelIdRef.current = panelId;

		// @ts-expect-error - TS2769 - No overload matches this call.
		return () => clearTimeout(delayRef.current);
	}, [panelId, rightSidebarCurrentState]);

	if (localPanelId !== EMBEDDED_CONFLUENCE_SIDEPANEL_CONTAINER_ID) {
		return null;
	}

	return <Box xcss={panelStyles}>{entryPoint}</Box>;
};

type SafeEmbeddedConfluencePanelProps = {
	/**
	 * Optional issueKey used to scope the embedded confluence side-panel's
	 * sweet-state container per work item. Supplied by callers that mount this
	 * panel **outside** the issue/work-item provider tree (e.g. the layout
	 * right-sidebar slot), so the panel doesn't have to reach into
	 * IssueContextContainer via `useIssueKey()`. Only consumed on the gate-on
	 * variant; the legacy variant scopes itself via `useIssueKey()`.
	 */
	issueKey?: string;
};

/**
 * Gate-on variant: scopes the panel's store by the route-derived `issueKey`
 * passed from the layout, so the panel can live outside the issue-view tree.
 */
const SafeEmbeddedConfluencePanelWithIssueKey = ({
	issueKey,
}: SafeEmbeddedConfluencePanelProps): React.JSX.Element => (
	<JSErrorBoundary
		fallback="unmount"
		id="right-side-embedded-confluence-panel"
		packageName="jiraIssueCreateConfluenceContent"
	>
		<EmbeddedConfluenceSidepanelContainerWithIssueKey issueKey={issueKey}>
			<PanelInLayout />
		</EmbeddedConfluenceSidepanelContainerWithIssueKey>
	</JSErrorBoundary>
);

/**
 * Gate-off variant: pre-migration behaviour — the container scopes itself via
 * `useIssueKey()`, which relies on the IssueContext being mirrored up to the
 * global store for layout-level consumers.
 */
const SafeEmbeddedConfluencePanelLegacy = (): React.JSX.Element => (
	<JSErrorBoundary
		fallback="unmount"
		id="right-side-embedded-confluence-panel"
		packageName="jiraIssueCreateConfluenceContent"
	>
		<EmbeddedConfluenceSidepanelContainer>
			<PanelInLayout />
		</EmbeddedConfluenceSidepanelContainer>
	</JSErrorBoundary>
);

export const SafeEmbeddedConfluencePanel: React.FC<SafeEmbeddedConfluencePanelProps> =
	componentWithFG(
		'remove-issue-context-mirror-on-wiv',
		SafeEmbeddedConfluencePanelWithIssueKey,
		SafeEmbeddedConfluencePanelLegacy,
	);

const panelStyles = xcss({
	padding: 'space.100',
	height: 'calc(100vh - (var(--topNavigationHeight, 0px) + var(--bannerHeight, 0px)))',
});
